import { X, Star, Check, Zap, Crown, CreditCard, Calendar, XCircle, UserMinus } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import { SUBSCRIPTION_PLANS } from '../services/subscriptionService';
import { format } from 'date-fns';

export interface SubscriptionCardProps {
  className?: string;
  /** Show Crown header (e.g. when used inside UpgradeModal) */
  showHeader?: boolean;
  /** When provided, show close button in header and call on close */
  onClose?: () => void;
  /** Optional override for Upgrade button; otherwise uses subscribeToPro from useSubscription */
  onUpgrade?: () => void;
}

/**
 * Single subscription UI matching UpgradeModal look and feel.
 * Use on Dashboard, inside UpgradeModal, and on Settings page.
 */
export function SubscriptionCard({
  className = '',
  showHeader = false,
  onClose,
  onUpgrade
}: SubscriptionCardProps) {
  const {
    profile,
    loading,
    isSubscribing,
    isCancelling,
    isProUser,
    subscribeToPro,
    openBillingPortal,
    cancelSubscription
  } = useSubscription();

  const handleUpgrade = onUpgrade ?? (() => subscribeToPro());

  if (loading) {
    return (
      <div className={`bg-white rounded-xl shadow-md p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2" />
          <div className="h-6 bg-gray-200 rounded w-1/2 mb-4" />
          <div className="h-20 bg-gray-200 rounded w-full" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={`bg-white rounded-xl shadow-md p-6 ${className}`}>
        <div className="text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to load subscription</h3>
          <p className="text-gray-600">Please try refreshing the page</p>
        </div>
      </div>
    );
  }

  const proPlan = SUBSCRIPTION_PLANS.pro;
  const rookiePlan = SUBSCRIPTION_PLANS.rookie;

  return (
    <div className={`bg-white rounded-xl shadow-md overflow-hidden ${className}`}>
      {showHeader && (
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-3 rounded-lg">
              <Crown className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Upgrade to Pro</h2>
              <p className="text-sm text-gray-600">
                Unlimited AI Coach, practice & game plans, lineup optimization, and more
              </p>
            </div>
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          )}
        </div>
      )}

      <div className="p-6">
        {/* Pro first, then Rookie – upgrade button between Pro and Rookie.
            Use auto-fit min width so layout responds to actual container width (not viewport). */}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(21rem,100%),1fr))] gap-6 mb-6">
          {/* Pro Plan + Upgrade button */}
          <div className="flex flex-col gap-4">
            <div className="border-2 border-primary rounded-lg p-6 relative bg-gradient-to-br from-primary/5 to-primary/10">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 flex justify-center">
                <span className="bg-primary text-white px-4 py-1 rounded-full text-sm font-semibold whitespace-nowrap">
                  {isProUser ? 'Current Subscription' : 'RECOMMENDED'}
                </span>
              </div>
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-primary">{proPlan.name}</h3>
                <div className="text-3xl font-bold text-primary mt-2">
                  ${proPlan.price}
                  <span className="text-sm font-normal text-gray-600">/month</span>
                </div>
              </div>
              <ul className="space-y-3">
                {proPlan.features.map((feature, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <Star className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700 font-medium">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            {!isProUser && (
              <button
                type="button"
                onClick={handleUpgrade}
                disabled={isSubscribing}
                className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-8 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isSubscribing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4" />
                    <span>Upgrade to Pro</span>
                  </>
                )}
              </button>
            )}
          </div>

          {/* Rookie Plan */}
          <div className="border border-gray-200 rounded-lg p-6">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-gray-700">{rookiePlan.name}</h3>
              <div className="text-3xl font-bold text-gray-900 mt-2">Free</div>
            </div>
            <ul className="space-y-3">
              {rookiePlan.features.map((feature, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-600">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

       
        {/* Pro: billing info */}
        {isProUser && (profile.current_period_end || profile.cancel_at_period_end) && (
          <div className="mb-6 p-3 bg-gray-50 rounded-lg">
            {profile.current_period_end && (
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="h-4 w-4 mr-1 shrink-0" />
                <span>Next billing: {format(new Date(profile.current_period_end), 'MMM d, yyyy')}</span>
              </div>
            )}
            {profile.cancel_at_period_end && (
              <div className="flex items-center text-sm text-orange-600 mt-1">
                <XCircle className="h-4 w-4 mr-1 shrink-0" />
                <span>Subscription will cancel at period end</span>
              </div>
            )}
          </div>
        )}

        {/* Actions – Pro only (Manage / Cancel) */}
        {isProUser && (
          <div className="space-y-2">
            <button
              type="button"
              onClick={openBillingPortal}
              className="w-full bg-gray-100 text-gray-700 py-2.5 px-4 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2"
            >
              <CreditCard className="h-4 w-4" />
              Manage Subscription
            </button>
            {!profile.cancel_at_period_end && (
              <button
                type="button"
                onClick={cancelSubscription}
                disabled={isCancelling}
                className="w-full border border-red-200 text-red-600 py-2.5 px-4 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isCancelling ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-t-transparent" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <UserMinus className="h-4 w-4" />
                    Cancel membership
                  </>
                )}
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
